import React, { useState, useEffect, useCallback } from 'react';
import { Lightbulb, RefreshCw, X } from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface FunFact {
  id: number;
  fact_text: string;
  category_name: string;
  fact_type: string;
}

const SESSION_KEY = 'didYouKnowDismissed';

export function DidYouKnowBanner() {
  const { selectedSite } = useSite();
  const [fact, setFact] = useState<FunFact | null>(null);
  const [shownIds, setShownIds] = useState<number[]>([]);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === 'true');
  const [loading, setLoading] = useState(false);

  const fetchFact = useCallback(async (excludeIds: number[]) => {
    if (!selectedSite?.id) return;
    setLoading(true);
    try {
      const params = excludeIds.length > 0 ? `?exclude=${excludeIds.join(',')}` : '';
      const res = await authFetch(`/aeo-wizard/sites/${selectedSite.id}/fun-fact${params}`);
      if (res.status === 204) {
        // No more facts — reset exclusion list and try once more
        if (excludeIds.length > 0) {
          setShownIds([]);
          const retry = await authFetch(`/aeo-wizard/sites/${selectedSite.id}/fun-fact`);
          if (retry.status === 204) {
            setFact(null);
          } else {
            const data = await retry.json();
            setFact(data);
            setShownIds([data.id]);
          }
        } else {
          setFact(null);
        }
      } else {
        const data = await res.json();
        setFact(data);
        setShownIds(prev => [...prev, data.id]);
      }
    } catch {
      // Non-critical UI feature
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id]);

  // Fetch on mount and site change
  useEffect(() => {
    if (dismissed) return;
    setShownIds([]);
    setFact(null);
    fetchFact([]);
  }, [selectedSite?.id, dismissed, fetchFact]);

  const handleRefresh = () => {
    fetchFact(shownIds);
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(SESSION_KEY, 'true');
  };

  if (!selectedSite?.id || dismissed || !fact) return null;

  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-50 px-4 py-3 dark:bg-amber-950/20">
      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-amber-800 dark:text-amber-300">
          <span className="font-medium">Did you know?</span>{' '}
          {fact.fact_text}
        </p>
        <p className="mt-1 text-xs text-amber-600/70 dark:text-amber-400/60">
          {fact.category_name} &middot; {fact.fact_type.replace('_', ' ')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="rounded p-1 text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/40"
          title="Show another fact"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={handleDismiss}
          className="rounded p-1 text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/40"
          title="Dismiss for this session"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
